import type {
  ProductDto,
  WorkspaceMemberWithUserDto,
} from '@up2stay/up2promo-api-client';
import {
  getBehaviorKindLabel,
  getPhysicalFormLabel,
  resolvePhysicalForm,
} from '@up2stay/up2promo-commons/products';
import { cn } from '@up2stay/up2promo-ui';

import {
  ResourceCardGrid,
  ResourceCardGridItem,
} from '@/core/components/layout';
import { scansLabelForCount, type ProductsDict } from '@/routes/app/products';

import { ProductListCard, ProductListCardSkeleton } from './product-list-card';

export type ProductCardListProps = {
  products: ProductDto[];
  dict?: ProductsDict;
  locationIdToName: Record<string, string>;
  userIdToMember: Record<string, WorkspaceMemberWithUserDto>;
  noLocationLabel?: string;
  noProfileLabel?: string;
  noTargetLabel?: string;
  copyHashLabel?: string;
  copyHashSuccess?: string;
  highlightedProductId?: string | null;
  /** Soften layout while a refetch keeps previous rows visible. */
  isRefreshing?: boolean;
  onProductClick: (product: ProductDto) => void;
};

export type ProductCardListSkeletonProps = {
  count?: number;
};

/** Loading placeholder that matches the live products grid density. */
export function ProductCardListSkeleton({
  count = 6,
}: ProductCardListSkeletonProps) {
  return (
    <ResourceCardGrid
      slotCount={count}
      asList={false}
      className="gap-3 md:gap-4"
      aria-busy
    >
      {Array.from({ length: count }).map((_, index) => (
        <ResourceCardGridItem key={index}>
          <ProductListCardSkeleton />
        </ResourceCardGridItem>
      ))}
    </ResourceCardGrid>
  );
}

export function ProductCardList({
  products,
  dict,
  locationIdToName,
  userIdToMember,
  noLocationLabel,
  noProfileLabel,
  noTargetLabel,
  copyHashLabel,
  copyHashSuccess,
  highlightedProductId,
  isRefreshing = false,
  onProductClick,
}: ProductCardListProps) {
  return (
    <ResourceCardGrid
      slotCount={products.length}
      asList
      className={cn(
        'gap-3 md:gap-4',
        isRefreshing && 'opacity-60 transition-opacity duration-150',
      )}
      aria-busy={isRefreshing || undefined}
    >
      {products.map((product) => {
        const form = resolvePhysicalForm(product);
        const scans30d = product.scanCount ?? 0;
        const scanSeries = product.scanSeries?.map((point) => ({
          date: point.date,
          count: point.count,
        }));
        const userId = product.target?.userId?.trim() || '';
        const locationId = product.target?.locationId;
        return (
          <ResourceCardGridItem key={product.id ?? product.hash}>
            <ProductListCard
              product={product}
              productKindLabel={getBehaviorKindLabel(product, dict?.edit)}
              formLabel={getPhysicalFormLabel(form, dict?.identity)}
              locationName={
                locationId
                  ? (locationIdToName[locationId] ?? locationId)
                  : undefined
              }
              assigneeMember={userId ? (userIdToMember[userId] ?? null) : null}
              noLocationLabel={noLocationLabel}
              noProfileLabel={noProfileLabel}
              noTargetLabel={noTargetLabel}
              scans30dLabel={scansLabelForCount(scans30d, dict, 'cardShort')}
              scans30d={scans30d}
              scanSeries={scanSeries}
              copyHashLabel={copyHashLabel}
              copyHashSuccess={copyHashSuccess}
              highlighted={
                !!highlightedProductId && product.id === highlightedProductId
              }
              onClick={() => onProductClick(product)}
            />
          </ResourceCardGridItem>
        );
      })}
    </ResourceCardGrid>
  );
}
